import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { TErrorStatuses } from '../../models';
import { TIME_SLOTS_DAYS_COUNT } from '../../constants';

import type { IApplicationSlice, TCreateThunkApi, IInitApp } from './models';
import { resetDelivery } from './delivery';
import { resetSms, setInitAttempts } from './sms';
import { resetFinish } from './finish';
import { initDraft } from './draft';
import { resetBasket } from './basket';
import { resetFirstStep } from './first-step';

const initialState: IApplicationSlice = {
    step: 'firstStep',
    status: undefined,
    device: undefined,
    appId: '',
    businessUid: '',
    requestBrokerageAccount: false,
    requestCodeWord: false,
    changePhoneButtonVisible: true,
    gentleErrorEnabled: false,
    userStatus: null,
    brokerageCheckbox: false,
    timeSlotDaysCount: TIME_SLOTS_DAYS_COUNT,
    lastDtPathStatus: null,
    appConfig: undefined,
};

export const applicationSlice = createSlice({
    name: 'application',
    initialState,
    reducers: {
        setStep: (state, action: PayloadAction<IApplicationSlice['step']>) => {
            state.step = action.payload;
        },
        setStatus: (state, action: PayloadAction<IApplicationSlice['status']>) => {
            state.status = action.payload;
        },
        setUserStatus: (state, action: PayloadAction<IApplicationSlice['userStatus']>) => {
            state.userStatus = action.payload;
        },
        setLastDtPathStatus: (
            state,
            action: PayloadAction<IApplicationSlice['lastDtPathStatus']>
        ) => {
            state.lastDtPathStatus = action.payload;
        },
        setDevice: (state, action: PayloadAction<IApplicationSlice['device']>) => {
            state.device = action.payload;
        },
        setTimeSlotDaysCount: (
            state,
            action: PayloadAction<IApplicationSlice['timeSlotDaysCount']>
        ) => {
            state.timeSlotDaysCount = action.payload;
        },
        setAppIds: (
            state,
            action: PayloadAction<Pick<IApplicationSlice, 'appId' | 'businessUid'>>
        ) => {
            state.appId = action.payload.appId;
            state.businessUid = action.payload.businessUid;
        },
        setRequestBrokerageAccount: (
            state,
            action: PayloadAction<IApplicationSlice['requestBrokerageAccount']>
        ) => {
            state.requestBrokerageAccount = action.payload;
        },
        setRequestCodeWord: (
            state,
            action: PayloadAction<IApplicationSlice['requestCodeWord']>
        ) => {
            state.requestCodeWord = action.payload;
        },
        setChangePhoneButtonVisible: (
            state,
            action: PayloadAction<IApplicationSlice['changePhoneButtonVisible']>
        ) => {
            state.changePhoneButtonVisible = action.payload;
        },
        setGentleErrorEnabled: (
            state,
            action: PayloadAction<IApplicationSlice['gentleErrorEnabled']>
        ) => {
            state.gentleErrorEnabled = action.payload;
        },
        setBrokerageCheckbox: (state, action: PayloadAction<boolean>) => {
            state.brokerageCheckbox = action.payload;
        },
        setAppConfig: (state, action: PayloadAction<IApplicationSlice['appConfig']>) => {
            state.appConfig = action.payload;
        },
    },
});

export const {
    setAppConfig,
    setAppIds,
    setBrokerageCheckbox,
    setChangePhoneButtonVisible,
    setDevice,
    setGentleErrorEnabled,
    setLastDtPathStatus,
    setRequestBrokerageAccount,
    setRequestCodeWord,
    setStatus,
    setStep,
    setTimeSlotDaysCount,
    setUserStatus,
} = applicationSlice.actions;

export const setErrorStep = createAsyncThunk<void, TErrorStatuses | undefined, TCreateThunkApi>(
    'setErrorStep',
    (status, { dispatch }) => {
        dispatch(setStep('error'));
        dispatch(setStatus(status));
        dispatch(resetSms());
        dispatch(resetDelivery());
        dispatch(resetFinish());
        dispatch(resetBasket());
        dispatch(resetFirstStep());
    }
);

export const initApp = createAsyncThunk<void, IInitApp, TCreateThunkApi>(
    'initApp',
    (
        {
            appConfig,
            device,
            draft,
            gentleErrorEnabled,
            prefilledFirstStep,
            smsConfirmationAttempts,
            timeSlotDaysCount,
        },
        { dispatch }
    ) => {
        dispatch(setDevice(device));
        dispatch(setGentleErrorEnabled(gentleErrorEnabled));
        dispatch(setInitAttempts(smsConfirmationAttempts));
        dispatch(initDraft(draft));
        dispatch(setAppConfig(appConfig));

        if (timeSlotDaysCount) {
            dispatch(setTimeSlotDaysCount(timeSlotDaysCount));
        }

        if (prefilledFirstStep) {
            const urlParams = new URLSearchParams(window?.location?.search);
            const appId = urlParams.get(prefilledFirstStep.queryNames.appId);
            const businessUid = urlParams.get(prefilledFirstStep.queryNames.businessUid);

            if (appId && businessUid) {
                dispatch(setAppIds({ appId, businessUid }));
                dispatch(setChangePhoneButtonVisible(false));
                dispatch(setStep('smsConfirm'));
            }
        }
    }
);
